import { useApi } from 'api/api-context';
import { FEATURE_TOGGLE_BROWSE_REVISION_TEMPLATES, FEATURE_TOGGLE_HR_MODULE, FEATURE_TOGGLE_PRICE_OFFERS } from 'env';

export interface OrganizationModuleAvailabilityInput {
  priceOffersEnabled: boolean;
  hrEnabled: boolean;
  revisionsEnabled: boolean;
  formsEnabled: boolean;
  revisionTemplatesEnabled: boolean;
  priceOffersDeploymentEnabled?: boolean;
  hrDeploymentEnabled?: boolean;
  revisionTemplatesDeploymentEnabled?: boolean;
}

export type OrganizationModuleFlags = Pick<
  OrganizationModuleAvailabilityInput,
  'priceOffersEnabled' | 'hrEnabled' | 'revisionsEnabled' | 'formsEnabled' | 'revisionTemplatesEnabled'
>;

export const ALL_ORGANIZATION_MODULES_ENABLED: OrganizationModuleFlags = {
  priceOffersEnabled: true,
  hrEnabled: true,
  revisionsEnabled: true,
  formsEnabled: true,
  revisionTemplatesEnabled: true,
};

export const ALL_ORGANIZATION_MODULES_DISABLED: OrganizationModuleFlags = {
  priceOffersEnabled: false,
  hrEnabled: false,
  revisionsEnabled: false,
  formsEnabled: false,
  revisionTemplatesEnabled: false,
};

/** Returns the API filter that limits a mixed document list to the enabled module kind. */
export function getEnabledDocumentKindFilter(revisionsEnabled: boolean, formsEnabled: boolean) {
  if (revisionsEnabled === formsEnabled) {
    return {};
  }

  return revisionsEnabled ? { 'kind.eq': 'revision' } : { 'kind.ne': 'revision' };
}

const PRICE_OFFERS_PERMISSION_SLUGS = new Set(['assign-price-offers', 'provide-price-offers']);
const HR_PERMISSION_SLUGS = new Set(['browse-hr', 'manage-hr', 'access-hr-files']);
const REVISION_PERMISSION_SLUGS = new Set([
  'assign-revisions',
  'approve-revisions',
  'approve-own-revisions',
  'fill-out-revisions',
  'self-assign-revisions',
  'accept-revisions',
  'accept-own-revisions',
  'browse-revisions',
  'generate-pdf-draft',
  'force-takeover-revisions',
  'download-archive-clauses',
  'access-files-revision',
]);
const FORM_PERMISSION_SLUGS = new Set([
  'assign-forms',
  'approve-forms',
  'approve-own-forms',
  'fill-out-forms',
  'self-assign-forms',
  'accept-forms',
  'accept-own-forms',
  'browse-forms',
  'generate-pdf-draft-forms',
  'form-service-report',
  'form-service-report-supplier',
  'force-takeover-forms',
  'access-files-form-service-report',
  'access-files-form-service-report-supplier',
]);
const REVISION_TEMPLATE_PERMISSION_SLUGS = new Set(['manage-templates']);

/**
 * Returns whether a permission can be assigned in an organization with the supplied raw module flags.
 */
export function isPermissionEnabledForOrganization(
  permissionSlug: string,
  { priceOffersEnabled, hrEnabled, revisionsEnabled, formsEnabled, revisionTemplatesEnabled }: OrganizationModuleFlags
) {
  if (PRICE_OFFERS_PERMISSION_SLUGS.has(permissionSlug)) {
    return priceOffersEnabled;
  }

  if (HR_PERMISSION_SLUGS.has(permissionSlug)) {
    return hrEnabled;
  }

  if (REVISION_PERMISSION_SLUGS.has(permissionSlug)) {
    return revisionsEnabled;
  }

  if (FORM_PERMISSION_SLUGS.has(permissionSlug)) {
    return formsEnabled;
  }

  if (permissionSlug === 'access-files-signed-document') {
    return revisionsEnabled || formsEnabled;
  }

  if (REVISION_TEMPLATE_PERMISSION_SLUGS.has(permissionSlug)) {
    return revisionTemplatesEnabled;
  }

  return true;
}

/**
 * Finds the permissions assignable to every selected target organization.
 */
export function combineOrganizationModuleFlags(flags: OrganizationModuleFlags[]): OrganizationModuleFlags {
  if (flags.length === 0) {
    return ALL_ORGANIZATION_MODULES_DISABLED;
  }

  return {
    priceOffersEnabled: flags.every(({ priceOffersEnabled }) => priceOffersEnabled),
    hrEnabled: flags.every(({ hrEnabled }) => hrEnabled),
    revisionsEnabled: flags.every(({ revisionsEnabled }) => revisionsEnabled),
    formsEnabled: flags.every(({ formsEnabled }) => formsEnabled),
    revisionTemplatesEnabled: flags.every(({ revisionTemplatesEnabled }) => revisionTemplatesEnabled),
  };
}

/**
 * Combines deployment-wide master switches with the current identity's direct-organization settings.
 */
export function getOrganizationModuleAvailability({
  priceOffersEnabled,
  hrEnabled,
  revisionsEnabled,
  formsEnabled,
  revisionTemplatesEnabled,
  priceOffersDeploymentEnabled = FEATURE_TOGGLE_PRICE_OFFERS,
  hrDeploymentEnabled = FEATURE_TOGGLE_HR_MODULE,
  revisionTemplatesDeploymentEnabled = FEATURE_TOGGLE_BROWSE_REVISION_TEMPLATES,
}: OrganizationModuleAvailabilityInput) {
  return {
    priceOffers: priceOffersDeploymentEnabled && priceOffersEnabled,
    hr: hrDeploymentEnabled && hrEnabled,
    revisions: revisionsEnabled,
    forms: formsEnabled,
    revisionTemplates: revisionTemplatesDeploymentEnabled && revisionTemplatesEnabled,
  };
}

/**
 * Returns effective organization-module availability for the active original or impersonated identity.
 */
export default function useOrganizationModules() {
  const { priceOffersEnabled, hrEnabled, revisionsEnabled, formsEnabled, revisionTemplatesEnabled } = useApi();

  return getOrganizationModuleAvailability({
    priceOffersEnabled,
    hrEnabled,
    revisionsEnabled,
    formsEnabled,
    revisionTemplatesEnabled,
  });
}
